import { Clock, Facebook, Instagram, Mail, MapPin, Phone } from "lucide-react";
import SEO from "../components/SEO";
import Reveal from "../components/Reveal";
import Breadcrumbs from "../components/Breadcrumbs";
import ServiceAreaMap from "../components/ServiceAreaMap";
import BookingEmbed from "../components/BookingEmbed";
import { BUSINESS, SERVICE_AREAS } from "../data/site";
import { track } from "../lib/api";

export default function Contact() {
  return (
    <>
      <SEO
        title="Contact Us"
        description="Contact Yavapai Waste Solutions in Prescott, AZ. Call (928) 713-2710 or email sales@ywsaz.com. Monday–Friday, 8 AM–5 PM."
        path="/contact"
      />
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Contact" }]} />
          <div className="mt-8 max-w-3xl">
            <h1 className="font-heading font-extrabold tracking-tight text-4xl sm:text-5xl text-slate-900 leading-[1.05]">Talk to a Real Person</h1>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              Call, email, or send a request — Nathaniel, Kay, and the YWS team are here during business hours.
            </p>
          </div>
        </div>
      </section>

      <section className="py-14 bg-slate-50" data-testid="contact-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-4">
            {[
              { icon: Phone, label: "Phone", value: BUSINESS.phone, href: BUSINESS.phoneHref, testid: "contact-phone", track: true },
              { icon: Mail, label: "Email", value: BUSINESS.email, href: `mailto:${BUSINESS.email}`, testid: "contact-email" },
              { icon: MapPin, label: "Location", value: BUSINESS.address, testid: "contact-address" },
              { icon: Clock, label: "Business Hours", value: BUSINESS.hours, testid: "contact-hours" },
            ].map((c, i) => (
              <Reveal key={c.label} delay={i * 0.06}>
                <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-start gap-4">
                  <span className="p-2.5 rounded-lg bg-sky-50 text-sky-700 shrink-0"><c.icon className="h-5 w-5" /></span>
                  <div>
                    <h2 className="font-heading font-semibold text-slate-900 text-sm">{c.label}</h2>
                    {c.href ? (
                      <a href={c.href} onClick={c.track ? () => track("click_to_call", { location: "contact" }) : undefined} className="text-slate-700 hover:text-sky-700 transition-colors font-medium" data-testid={c.testid}>
                        {c.value}
                      </a>
                    ) : (
                      <p className="text-slate-700 font-medium" data-testid={c.testid}>{c.value}</p>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.25}>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h2 className="font-heading font-semibold text-slate-900 text-sm mb-3">Follow YWS</h2>
                <div className="flex gap-3">
                  <a href={BUSINESS.facebook} target="_blank" rel="noreferrer" aria-label="YWS on Facebook" className="p-2.5 rounded-lg bg-slate-100 text-slate-700 hover:bg-sky-600 hover:text-white transition-colors" data-testid="contact-facebook">
                    <Facebook className="h-5 w-5" />
                  </a>
                  <a href={BUSINESS.instagram} target="_blank" rel="noreferrer" aria-label="YWS on Instagram" className="p-2.5 rounded-lg bg-slate-100 text-slate-700 hover:bg-sky-600 hover:text-white transition-colors" data-testid="contact-instagram">
                    <Instagram className="h-5 w-5" />
                  </a>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h2 className="font-heading font-semibold text-slate-900 text-sm mb-2">Service Areas</h2>
                <p className="text-sm text-slate-600 leading-relaxed">{SERVICE_AREAS.map((a) => a.name).join(" · ")}</p>
                <div className="mt-4">
                  <ServiceAreaMap compact />
                </div>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-3">
            <Reveal delay={0.1}>
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <BookingEmbed heightClass="h-[75vh] min-h-[560px]" />
              </div>
              <p className="mt-4 text-sm text-slate-500 text-center">
                Outside business hours, calls go to voicemail — we'll get back to you the next business day.
              </p>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
